import { daysUntil } from "@/lib/evaluation-term-utils"
import {
  EVALUATION_REFERENCE_DATE,
  getTermsByPosition,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"

export interface EvaluationWorkQueueRow {
  id: string
  termLabel: string
  programName: string
  subject: string
  state: "notSetup" | "draft" | "scheduled" | "live" | "closed" | "review" | "attention"
  stateLabel: string
  responseRate?: number
  dueLabel?: string
  actionLabel: string
  priority: number
}

const STATE_LABELS: Record<EvaluationWorkQueueRow["state"], string> = {
  notSetup: "Not set up",
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  closed: "Closed",
  review: "Review feedback",
  attention: "Below threshold",
}

export function buildTermWorkQueueRows(
  term: EvaluationTerm,
  refDate: Date = EVALUATION_REFERENCE_DATE,
): EvaluationWorkQueueRow[] {
  return rowsFromTerm(term, refDate)
}

function rowsFromTerm(term: EvaluationTerm, refDate: Date): EvaluationWorkQueueRow[] {
  const rows: EvaluationWorkQueueRow[] = []
  const termLabel = `${term.academicYear} · ${term.termName}`

  if (term.counts.notSetup > 0) {
    rows.push({
      id: `${term.id}-setup`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.notSetup} courses`,
      state: "notSetup",
      stateLabel: STATE_LABELS.notSetup,
      actionLabel: "Schedule evaluations",
      priority: 1,
    })
  }

  if (term.counts.draft > 0) {
    rows.push({
      id: `${term.id}-draft`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.draft} evaluations`,
      state: "draft",
      stateLabel: STATE_LABELS.draft,
      actionLabel: "Finish setup",
      priority: 2,
    })
  }

  if (term.counts.scheduled > 0) {
    rows.push({
      id: `${term.id}-scheduled`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.scheduled} courses`,
      state: "scheduled",
      stateLabel: STATE_LABELS.scheduled,
      dueLabel: term.nextScheduledDate
        ? `Sends in ${daysUntil(term.nextScheduledDate, refDate)} days`
        : undefined,
      actionLabel: "Update schedule",
      priority: 3,
    })
  }

  if (term.counts.live > 0) {
    rows.push({
      id: `${term.id}-live`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.live} evaluations`,
      state: "live",
      stateLabel: STATE_LABELS.live,
      responseRate: term.liveResponseRate,
      dueLabel: term.nextClosingDate
        ? `Closes in ${daysUntil(term.nextClosingDate, refDate)} days`
        : undefined,
      actionLabel: "Remind or extend",
      priority: 4,
    })
  }

  if (term.counts.closed > 0 && term.timelinePosition === "current") {
    rows.push({
      id: `${term.id}-closed`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.closed} evaluations`,
      state: "closed",
      stateLabel: STATE_LABELS.closed,
      responseRate: term.closedResponseRate,
      actionLabel: "Review feedback",
      priority: 5,
    })
  }

  if (
    term.counts.closed > 0 &&
    term.counts.published === 0 &&
    term.timelinePosition === "last"
  ) {
    rows.push({
      id: `${term.id}-publish`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.closed} evaluations`,
      state: "review",
      stateLabel: "Ready to publish",
      responseRate: term.closedResponseRate,
      actionLabel: "Publish results",
      priority: 5,
    })
  }

  if (term.needsAttention && term.attention) {
    rows.push({
      id: `${term.id}-attention`,
      termLabel,
      programName: term.programName,
      subject: `${term.attention.coursesBelowThreshold} courses · ${term.attention.facultyRolesBelowThreshold} faculty roles`,
      state: "attention",
      stateLabel: STATE_LABELS.attention,
      responseRate: term.closedResponseRate,
      actionLabel: "View analytics",
      priority: 0,
    })
  }

  return rows
}

export function buildEvaluationWorkQueue(): EvaluationWorkQueueRow[] {
  const refDate = EVALUATION_REFERENCE_DATE
  const terms = [
    ...getTermsByPosition("current"),
    ...getTermsByPosition("last"),
    ...getTermsByPosition("upcoming"),
  ]

  return terms
    .flatMap(term => buildTermWorkQueueRows(term, refDate))
    .sort((a, b) => a.priority - b.priority)
}
